/**
 * Licensed Materials - Property of IBM
 * IBM Watson Analytics (C) Copyright IBM Corp. 2017, 2022
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define(['../../lib/@waca/core-client/js/core-client/ui/core/Class',
	'./FilterQueryHelper',
	'./FilterErrorView',
	'../FilterMetadataHelper',
	'../../widgets/livewidget/nls/StringResources',
	'../../util/DashboardFormatter',
	'underscore'
], function(BaseClass, FilterQueryHelper, FilterErrorView, FilterMetadataHelper, resources, Formatter, _) {
	'use strict';

	/**
	 * The controller holds the business logic for the range filter
	 */
	var FilterBaseController = BaseClass.extend({

		MIN_HEIGHT: 275, // Min height of common control
		HEIGHT: 500, // Height of common contro;

		init: function(params, extraOptions) {
			FilterBaseController.inherited('init', this, arguments);

			this.dashboardApi = params.dashboardApi;

			this.ajaxSvc = params.ajaxSvc || this.dashboardApi.getGlassCoreSvc('.Ajax');

			this.logger = this.dashboardApi.getGlassCoreSvc('.Logger');

			this.id = _.uniqueId('FilterBase');
			this.tabId = params.containerPageId;
			this.listenToGroupId = params.listenToGroupId;
			this.scope = (this.tabId === 'global' || !this.listenToGroupId) ? this.tabId : (this.tabId + '_' + this.listenToGroupId);

			this.pageContextApi = this.dashboardApi.getFeature('PageContextService').getPageContext();

			var properties = params.initialConfigJSON && params.initialConfigJSON.properties;
			if(!this._initializeItemContext(properties, params.itemContext)){
				return this.processError();
			}else {
				//Register this control for applying addOns (such as cascading filters) via the FilterControlAddOns feature.
				this.filterControlAddOns = this.dashboardApi.getFeature('FilterControlAddOns');
				this.filterControlAddOns && this.filterControlAddOns.registerFilterControl({ columnInfo: this._normalizeItemContext(), updateHandler: { handlerName: '_updateFilterDataOnPageContextChange', ownerObject: this } });

				this.metadataInfo = this._getMetadataInfo(params);
				this.queryHelper = (params.queryHelper) ? params.queryHelper : this._createQueryHelper();

				this.dashboardApi.on('pagecontext:filterContextUpdated', this.processContextChange, this);
				this.dashboardApi.on('shapingmodel:changed', this.onShapingModelChanged, this);

				// Indicates if a filter is active. This state is persited. An inactive
				// filter (isFilterActive = false) will not update the page context
				// on creation.
				this.isFilterActive = (!params.initialConfigJSON || _.isUndefined(params.initialConfigJSON.isFilterActive)) ? true : params.initialConfigJSON.isFilterActive;
				this.widgetModel = params.model;

				this.commonView = (extraOptions && extraOptions.commonView) || params.commonView;
				this.viewConfig = {
					commonView: this.commonView,
					isCompact: params.isCompact || false,
					dashboardApi: this.dashboardApi,
					showHeader: params.showHeader
				};
			}
		},

		/**
		 * @description By default, the queryHelper filters the results for a filter item by any corresponding global filter on that same item.
		 * In the current dashboard design, this should not apply for local filters.
		 * @param {Boolean} flag - set true to apply the corresponding filter, false otherwise.
		 */
		setApplyCorrespondingGlobalFilter(flag=true) {
			this.queryHelper && this.queryHelper.setApplyCorrespondingGlobalFilter && this.queryHelper.setApplyCorrespondingGlobalFilter(flag);
		},

		/**
		 * @return {Promise}
		 */
		initialize: function() {
			return Promise.reject(false /* abstract function, subclass must override */ );
		},

		onShow: function() {
			return this.render();
		},

		_initializeItemContext: function(properties, itemContext) {
			if ((!itemContext && !properties) ||
				//An itemId must be defined for a filter to be valid.
				(properties && !properties.itemId)) {
				return false;
			} else if(!itemContext){
				//endor need common item context generator loagic here
				this.itemContext  = _.pick(properties, 'sourceId', 'itemId', 'itemName',
					'dataType', 'isHierarchy', 'isNamedSet');
				this.itemContext.scope = this.scope;
				this.itemContext.isHierarchy = properties.isHierarchy || false;
				var pageContextItem = this.getCurrentPageContextItem();
				if (!pageContextItem) {
					this.itemContext.filterName = this.itemContext.itemName;
				}
			}else {
				this.itemContext = itemContext;
			}
			return true;
		},

		_initializePageContext: function(isRange) {
			var pageContextItem = this.getCurrentPageContextItem();
			if (!pageContextItem && this.isFilterActive) {
				var updatePayload = {
					command: 'update'
				};

				if (isRange) {
					updatePayload.condition = {};
				} else {
					updatePayload.values = [];
				}

				this.pageContextApi.updateFilterContext(this.itemContext, updatePayload);
			}
		},

		_initializeExtraData: function() {
			// stub class for subclasses
		},

		processError: function(error) {
			return this.renderError(error);
		},

		_createQueryHelper: function(options) {
			var params = {
				dashboardApi: this.dashboardApi,
				ajaxSvc: this.ajaxSvc,
				itemContext: this.itemContext,
				pageContextApi: this.pageContextApi,
				metadataInfo: this.metadataInfo,
				globalFilters: this.dashboardApi.getFeature('GlobalFilters'),
				queryService: this.dashboardApi.getFeature('QueryService')
			};

			if (options) {
				_.extend(params, options, this.queryHelperOptions);
			}
			var queryHelper = new FilterQueryHelper(params);
			return queryHelper;
		},

		/**
		 * This function is called whenever the page context changes.
		 * @param pageContextResultPayload - the payload of a pageContextChanged or filterContextChanged event.
		 */
		processContextChange: function(pageContextResultPayload) {
			if (this.getCurrentPageContextItem() && (!pageContextResultPayload.senderMatches(this.id) || pageContextResultPayload.isClear())) {

				var relateToMe = pageContextResultPayload.containsItem(this.getCurrentPageContextItem()) || this._addOnNeedsUpdate(pageContextResultPayload),
					relateToMyGlobal = pageContextResultPayload.containsItem(this._getGlobalPageContextItem()),
					isDelete = pageContextResultPayload.isDelete();

				// Page context item for the filter widget instance has been deleted.
				// Can occur when the tab level filter item is deleted from the filter dock and there is a
				// filter widget equvalent on the dashboard.
				if (isDelete && relateToMe) {
					this.isFilterActive = false;
					this.pageContextItem = null;
				} else if (isDelete && relateToMyGlobal) {
					//The global page context item was deleted.
					this.globalPageContextItem = null;
				}

				// If related, get new filter data
				return (relateToMe || relateToMyGlobal) ? this._updateFilterDataOnPageContextChange().then(this.updateSavedState.bind(this)) : Promise.resolve(null);
			}
			return Promise.resolve(null);
		},

		_normalizeItemContext() {
			return {
				columnId: this.itemContext.itemId,
				dataSourceId: this.itemContext.sourceId,
				scope: this.itemContext.scope
			};
		},

		_normalizePageContextItem(pageContextItem) {
			return {
				columnId: pageContextItem.getItemId(),
				dataSourceId: pageContextItem.getSourceId(),
				scope: pageContextItem.getScope()
			};
		},

		_addOnNeedsUpdate(pageContextResultPayload) {
			return pageContextResultPayload.getItems().some((pageContextItem) => {
				return this.filterControlAddOns.updateNeeded(this._normalizePageContextItem(pageContextItem), this._normalizeItemContext());
			});
		},

		_canApplyShapingModelChanged: function(sourceId) {
			return sourceId && this.itemContext && sourceId === this.itemContext.sourceId;
		},

		onShapingModelChanged: function(payload) {
			payload = payload || {};
			if (this._canApplyShapingModelChanged(payload.sourceId)) {
				return this.refreshDataAndView(payload);
			} else {
				return Promise.resolve();
			}
		},

		refreshDataAndView: function() {
			throw new Error('abstract function, subclass must override');
		},

		/**
		 * This function is called by processContextChange if page context change impacts current filter.
		 * @return {Promise}
		 */
		_updateFilterDataOnPageContextChange: function() {
			return Promise.reject(false /* abstract function, subclass must override */ );
		},

		onOpen: function() {
			if (this.view && this.view.doRender) {
				this.view.doRender();
			}
		},

		/**
		 * Commence the render sequence of filter view.
		 *
		 * @return {Promise} Resolves when render sequence is complete.
		 */
		render: function() {
			if (!this.view) {
				return Promise.reject('View does not exist.');
			}
			return this.view.renderViewComposition()
				.catch(this.logger.error.bind(this.logger));
		},

		/**
		 * Render error view
		 *
		 * @return {Promise} Resolves when rendering error operation is complete
		 */
		renderError: function(error) {
			try {
				if (this.view && this.view.remove) {
					this.view.remove();
				}
				this.view = null;
			} catch (e) {
				this.logger.error(e);
				this.view = null;
			}

			this.errorView = new FilterErrorView(this.viewConfig);
			return this.errorView.renderViewComposition(error);
		},

		/**
		 * @return boolean returns true if new view or model is created
		 */
		_ensureFilterViewExists: function() {
			var created = false;
			if (this.errorView) {
				this.errorView.remove();
				this.errorView = null;
			}

			if (!this.model) {
				this._createFilterModel();
				created = true;
			}

			if (!this.view) {
				// View can be set to null by renderError. Create here if does not exist
				this._createFilterView();
				created = true;
			}

			return created;
		},

		_createFilterView: function() {
			//subclass must override
		},
		_createFilterModel: function() {
			//subclass must override
		},

		/**
		 * @returns the pageContextItem that this FilterBaseController controls from its itemContext (or null if no pageContextItem exists yet.)
		 */
		getCurrentPageContextItem: function() {
			if (!this.pageContextItem) {
				this.pageContextItem = this.itemContext ? this.pageContextApi.getPageContextItem(this.itemContext) : null;
			}
			return this.pageContextItem;
		},

		_getGlobalPageContextItem: function() {

			if (this.globalPageContextItem) {
				return this.globalPageContextItem;
			}

			if (this.itemContext.scope && this.itemContext.scope !== 'global') {
				//If this member filter is a tab filter, its member trees must be limited to any
				//selected global filter values for the same item.  Obtain the corresponding global context item by
				//changing scope to global
				var globalItemContext = _.clone(this.itemContext);
				globalItemContext.scope = 'global';
				this.globalPageContextItem = this.pageContextApi.getPageContextItem(globalItemContext);
				return this.globalPageContextItem;
			}
			return null;
		},

		_getMetadataInfo: function(params){
			let metadataInfo = null;
			let item = null;
			if(params.metadataColumn || params.slot){
				metadataInfo =  FilterMetadataHelper.getFilterMetadataInfo(params.metadataColumn, params.slot, params.mapIndex);
			} else {
				item = this.getCurrentPageContextItem();
				metadataInfo = item ?  item.getMetadataInfo() : null;
			}

			if(!metadataInfo || _.isEmpty(metadataInfo)){

				//Only check for the existence of an item
				let columnName =  item ? item.getItemId() : Formatter.format(null);

				throw new Error(resources.get('errMsgFilterItemMissingTitle', {
					itemName: columnName
				}));
			}
			return metadataInfo;
		},

		remove: function() {
			if (this.view) {
				if (this.view.remove) {
					this.view.remove();
				}
				this.view = null;
			}

			if (this.errorView) {
				if (this.errorView.remove) {
					this.errorView.remove();
				}
				this.errorView = null;
			}

			this.model = null;

			this.id = null;

			this.queryHelper = null;

			if (this.dashboardApi) {
				this.dashboardApi.off('pagecontext:filterContextUpdated', this.processContextChange, this);
				this.dashboardApi.off('shapingmodel:changed', this.onShapingModelChanged, this);
				this.dashboardApi = null;
			}

			this.filterControlAddOns && this.filterControlAddOns.deregisterFilterControl({ columnInfo: this._normalizeItemContext(), updateHandler: { handlerName: '_updateFilterDataOnPageContextChange', ownerObject: this } });
			this.filterControlAddOns = null;
			this.pageContextApi = null;

			this.itemContext = null;
			this.globalPageContextItem = null;
			this.pageContextItem = null;
		},

		setLoading: function() {
			if (this.view) {
				this.view.loading = true;
			}
			if (this.model) {
				this.model.trigger('change:loading');
			}
		},

		removeLoading: function() {
			if (this.view) {
				this.view.loading = false;
			}
			if (this.model) {
				this.model.trigger('change:loading');
			}
		},

		isOpen: function() {
			return this.view && this.view.isOpen();
		},
		/**
		 * Common Control Callback handler
		 */
		onClose: function() {
			this.closed = true;
			this.view.remove();
		},
		/**
		 * Common Control Callback handler
		 */
		onApply: function() {
			this.updateSavedState();
			// Defect 188518 (see comments): 'OK' button invokes onApply callback then invokes onDone callback which will be revisited to streamline apis.
			// 'Apply' botton on compact mode for Filter widget invokes onApply callback correctly without calling onDone.
			// Until the defect is fixed, we need to execute onApply function only in compact mode.
			if (this.view.isCompact) {
				this.onDone();
				this.view.renderViewComposition();
			}
		},

		onInitializationDone: function() {
			this.removeLoading();
			this.updateSavedState();
		},

		updateSavedState: function() {
			this.model && this.model.set({ savedState: JSON.stringify(this.model.toJSON()) });
		},

		createRenderDeferred() {
			this._renderDeferred = {};

			this._renderDeferred.promise = new Promise((resolve, reject) => {
				this._renderDeferred.resolve = resolve;
				this._renderDeferred.reject = reject;
			});
		}

	});
	return FilterBaseController;
});
